import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import keycloak, {
  initKeycloak,
  getToken,
  getUserProfile,
  getSessionId,
  logout,
  login,
  getCurrentUser,
} from "../utils/auth";
import { fetchUserGroups, pingLastLogin } from "../utils/api";

// Marks the SSO session whose login has already been reported to the server.
const LOGIN_PING_SESSION_KEY = "lastLoginPingSid";

const AuthContext = createContext({
  isAuthenticated: false,
  user: null,
  token: null,
  roles: [],
  groups: [],
  isAdmin: false,
  refresh: async () => {},
  login: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const refreshState = async () => {
    const t = await getToken();
    setToken(t);
    const profile = await getUserProfile();

    // Fetch additional groups from API if user is authenticated
    let apiGroups = [];
    if (t && profile) {
      try {
        apiGroups = await fetchUserGroups();
      } catch (error) {
        console.error("Error fetching user groups from API:", error);
        // Fall back to groups from token
        apiGroups = profile.groups || [];
      }
    }

    // Merge groups from token and API
    const mergedProfile = {
      ...profile,
      groups: [...(profile?.groups || []), ...apiGroups].filter(
        (group, index, self) => self.indexOf(group) === index // Remove duplicates
      ),
    };

    setUser(mergedProfile);
    setIsAuthenticated(!!t);
  };

  // Report the login once per SSO session.
  // check-sso resolves on every page load, so without this a refresh loop would
  // become a write loop. The server throttles as well, which is what makes this
  // safe to skip when sessionStorage is unavailable or was cleared.
  const recordLoginOnce = async () => {
    try {
      const sid = await getSessionId();
      if (sid && sessionStorage.getItem(LOGIN_PING_SESSION_KEY) === sid) {
        return;
      }

      await pingLastLogin();

      if (sid) {
        sessionStorage.setItem(LOGIN_PING_SESSION_KEY, sid);
      }
    } catch (error) {
      // Best effort only: a failed ping must never keep the user out of the app.
      console.error("Error recording last login:", error);
    }
  };

  useEffect(() => {
    (async () => {
      const ok = await initKeycloak();
      if (ok) {
        await refreshState();
        recordLoginOnce();
      } else {
        setIsAuthenticated(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      isAuthenticated,
      user: user || getCurrentUser(),
      token,
      roles: user?.roles || [],
      groups: user?.groups || [],
      isAdmin: (user?.roles || []).includes("tamuAdmin"),
      refresh: refreshState,
      login,
      logout,
    }),
    [isAuthenticated, user, token]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
